import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';
import { AppError } from 'entities/Errors';

import {
  getConsumerUnitList,
  getConsumerUnitInvoices,
  getConsumerUnit,
  updateConsumerUnit,
  uploadConsumerUnitImage,
  deleteConsumerUnit,
  createConsumerUnit,
} from './consumer_unit.action';

interface ConsumerUnitState {
  fetchLoading: boolean;
  listLoading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  updateImageLoading: boolean;
  invoicesLoading: boolean;
  selectedConsumerUnitIds: string[];
  consumerUnit: ConsumerUnit | null;
  consumerUnitDeleted: ConsumerUnit | null;
  consumerUnitList: ConsumerUnit[];
  consumerUnitUpdated: ConsumerUnit | null;
  consumerUnitInvoiceList: Invoice[];
  error: AppError | null;
  refresh: boolean;
}

const initialState = {
  fetchLoading: false,
  createLoading: false,
  listLoading: false,
  updateLoading: false,
  deleteLoading: false,
  updateImageLoading: false,
  invoicesLoading: false,
  error: null,
  selectedConsumerUnitIds: [],
  consumerUnit: null,
  consumerUnitUpdated: null,
  consumerUnitDeleted: null,
  consumerUnitList: [],
  consumerUnitInvoiceList: [],
  // lastInvoice: null,
  refresh: false,
} as ConsumerUnitState;

export const ConsumerUnitSlice = createSlice({
  name: 'consumerUnit',
  initialState,
  reducers: {
    setRefresh: (state, action: PayloadAction<{ refresh: boolean }>) => {
      // state.refresh = action.payload.refresh;
      state.refresh = action.payload.refresh;
      if (action.payload.refresh) {
        state.consumerUnit = null;
        state.consumerUnitUpdated = null;
        state.consumerUnitDeleted = null;
        state.error = null;
        // state.refresh = false;
      }
    },
    addSelectedConsumerUnitId: (
      state,
      action: PayloadAction<{ id: string }>
    ) => {
      state.selectedConsumerUnitIds = [
        ...state.selectedConsumerUnitIds,
        action.payload.id,
      ];
    },
    removeSelectedConsumerUnitId: (
      state,
      action: PayloadAction<{ id: string }>
    ) => {
      const selectedIds = state.selectedConsumerUnitIds.filter(
        (x) => x !== action.payload.id
      );
      state.selectedConsumerUnitIds = [...selectedIds];
    },
    clearSelectedConsumerUnitIds: (state) => {
      state.selectedConsumerUnitIds = [];
    },
    // Favorito e ordem (out/2026): atualização otimista da lista — a API
    // grava em seguida e, se falhar, a tela recarrega a lista.
    setConsumerUnitFavourite: (
      state,
      action: PayloadAction<{ id: string; isFavourite: boolean }>
    ) => {
      const uc = state.consumerUnitList.find((x) => x.id === action.payload.id);
      if (uc) uc.isFavourite = action.payload.isFavourite;
    },
    setConsumerUnitOrder: (state, action: PayloadAction<{ ids: string[] }>) => {
      action.payload.ids.forEach((id, index) => {
        const uc = state.consumerUnitList.find((x) => x.id === id);
        if (uc) uc.displayOrder = index;
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getConsumerUnit.pending, (state) => {
        state.fetchLoading = true;
        state.consumerUnit = null;
        state.error = null;
      })
      .addCase(getConsumerUnit.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.consumerUnit = action.payload.data;
      })
      .addCase(getConsumerUnit.rejected, (state, action) => {
        state.fetchLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(getConsumerUnitList.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(getConsumerUnitList.fulfilled, (state, action) => {
        state.listLoading = false;
        state.consumerUnitList = action.payload.data;
      })
      .addCase(getConsumerUnitList.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(getConsumerUnitInvoices.pending, (state) => {
        state.invoicesLoading = true;
        state.error = null;
      })
      .addCase(getConsumerUnitInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.consumerUnitInvoiceList = action.payload.data;
      })
      .addCase(getConsumerUnitInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(createConsumerUnit.pending, (state) => {
        state.createLoading = true;
        state.consumerUnit = null;
        state.error = null;
      })
      .addCase(createConsumerUnit.fulfilled, (state, action) => {
        state.consumerUnit = action.payload.data;
        state.createLoading = false;
      })
      .addCase(createConsumerUnit.rejected, (state, action) => {
        state.error = action.payload as AppError;
        state.createLoading = false;
      });

    builder
      .addCase(updateConsumerUnit.pending, (state) => {
        state.updateLoading = true;
        state.consumerUnitUpdated = null;
        state.error = null;
      })
      .addCase(updateConsumerUnit.fulfilled, (state, action) => {
        state.consumerUnitUpdated = action.payload.data;
        state.updateLoading = false;
      })
      .addCase(updateConsumerUnit.rejected, (state, action) => {
        state.error = action.payload as AppError;
        state.updateLoading = false;
      });

    builder
      .addCase(uploadConsumerUnitImage.pending, (state) => {
        state.updateImageLoading = true;
        state.error = null;
      })
      .addCase(uploadConsumerUnitImage.fulfilled, (state, action) => {
        state.consumerUnit = action.payload.data;
        state.updateImageLoading = false;
      })
      .addCase(uploadConsumerUnitImage.rejected, (state, action) => {
        state.updateImageLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(deleteConsumerUnit.pending, (state) => {
        state.error = null;
        state.deleteLoading = true;
        state.consumerUnitDeleted = null;
      })
      .addCase(deleteConsumerUnit.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.consumerUnitDeleted = action.payload.data;
      })
      .addCase(deleteConsumerUnit.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as AppError;
      });
  },
});

export const {
  setRefresh: setConsumerUnitRefresh,
  addSelectedConsumerUnitId,
  removeSelectedConsumerUnitId,
  clearSelectedConsumerUnitIds,
  setConsumerUnitFavourite,
  setConsumerUnitOrder,
} = ConsumerUnitSlice.actions;

export default ConsumerUnitSlice.reducer;
